import { createContext, useContext, useState, useRef, useCallback, type ReactNode } from 'react'

interface AnimationTargets {
  shardsCounter: HTMLElement | null
  streakCounter: HTMLElement | null
}

// Split context into Actions and State to prevent unnecessary re-renders
// Components that only trigger animations won't re-render when animation state changes

interface CelebrationAnimationActionsValue {
  // Target refs for animations
  registerShardsCounter: (element: HTMLElement | null) => void
  registerStreakCounter: (element: HTMLElement | null) => void
  
  // Trigger animations
  triggerGemAnimation: (amount: number, isFirstOfDay: boolean, newShardValue: number) => void
  triggerStreakExplosion: () => void
  
  // Optimistic streak update for immediate UI feedback
  setOptimisticStreakIncrement: () => void
  clearOptimisticStreak: () => void
  
  // Track if streak was already incremented today (prevents multiple optimistic updates)
  markStreakIncrementedToday: () => void
  
  // TopBar visibility tracking
  setTopBarVisible: (visible: boolean) => void
  
  // Floating wallet callbacks
  onFloatingWalletComplete: () => void
}

interface CelebrationAnimationStateValue {
  // Optimistic streak delta
  optimisticStreakDelta: number
  hasIncrementedStreakToday: boolean
  
  // State for animation components to consume
  gemAnimation: {
    active: boolean
    amount: number
    targetPosition: { x: number; y: number } | null
    newValue: number
  }
  streakExplosion: {
    active: boolean
    targetPosition: { x: number; y: number } | null
  }
  shardCounterUpdate: {
    active: boolean
    newValue: number
  }
  floatingWallet: {
    active: boolean
    startValue: number
    endValue: number
  }
  isTopBarVisible: boolean
}

const CelebrationAnimationActionsContext = createContext<CelebrationAnimationActionsValue | undefined>(undefined)
const CelebrationAnimationStateContext = createContext<CelebrationAnimationStateValue | undefined>(undefined)

// Get today's date string in user's timezone for tracking daily streak increment
function getTodayDateString(): string {
  return new Date().toLocaleDateString('en-CA') // Returns YYYY-MM-DD format
}

export function CelebrationAnimationProvider({ children }: { children: ReactNode }) {
  const targetsRef = useRef<AnimationTargets>({
    shardsCounter: null,
    streakCounter: null,
  })
  
  const [gemAnimation, setGemAnimation] = useState({
    active: false,
    amount: 0,
    targetPosition: null as { x: number; y: number } | null,
    newValue: 0,
  })
  
  const [streakExplosion, setStreakExplosion] = useState({
    active: false,
    targetPosition: null as { x: number; y: number } | null,
  })
  
  const [shardCounterUpdate, setShardCounterUpdate] = useState({
    active: false,
    newValue: 0,
  })
  
  const [floatingWallet, setFloatingWallet] = useState({
    active: false,
    startValue: 0,
    endValue: 0,
  })
  
  const [isTopBarVisible, setIsTopBarVisible] = useState(true)
  
  // Optimistic streak delta - added to the display streak immediately
  const [optimisticStreakDelta, setOptimisticStreakDelta] = useState(0)
  
  // Track if we've already done an optimistic streak increment today
  // This prevents multiple optimistic updates when user completes multiple habits
  const [streakIncrementDate, setStreakIncrementDate] = useState<string | null>(null)
  
  const hasIncrementedStreakToday = streakIncrementDate === getTodayDateString()
  
  // Track TopBar visibility state in a ref for access in triggerGemAnimation
  const isTopBarVisibleRef = useRef(true)
  
  const setTopBarVisible = useCallback((visible: boolean) => {
    isTopBarVisibleRef.current = visible
    setIsTopBarVisible(visible)
  }, [])
  
  const registerShardsCounter = useCallback((element: HTMLElement | null) => {
    targetsRef.current.shardsCounter = element
  }, [])
  
  const registerStreakCounter = useCallback((element: HTMLElement | null) => {
    targetsRef.current.streakCounter = element
  }, [])
  
  const getElementPosition = (element: HTMLElement | null): { x: number; y: number } | null => {
    if (!element) {
      // Fallback positions based on viewport - approximate where the counters are
      if (typeof window !== 'undefined') {
        const isDesktop = window.innerWidth >= 1024
        if (isDesktop) {
          // Desktop: Sidebar is on the left, counters are near bottom
          return { x: 32, y: window.innerHeight - 120 }
        } else {
          // Mobile: TopBar is at the top, counters are on the right
          return { x: window.innerWidth - 60, y: 40 }
        }
      }
      return null
    }
    const rect = element.getBoundingClientRect()
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    }
  }
  
  const triggerGemAnimation = useCallback((amount: number, isFirstOfDay: boolean, newShardValue: number) => {
    const targetPosition = getElementPosition(targetsRef.current.shardsCounter)
    
    // Ensure amount is a valid number (defensive against NaN)
    const safeAmount = typeof amount === 'number' && !isNaN(amount) && isFinite(amount) ? amount : 0
    const safeNewShardValue = typeof newShardValue === 'number' && !isNaN(newShardValue) && isFinite(newShardValue) ? newShardValue : 0
    
    // Skip animation if amount is 0 or invalid
    if (safeAmount <= 0) {
      console.warn('[CelebrationAnimation] Skipping gem animation - invalid amount:', amount)
      return
    }
    
    // Get current shard value from the counter element's text content
    // If not available, use the newShardValue - amount as fallback
    let currentShards = 0
    if (targetsRef.current.shardsCounter) {
      const text = targetsRef.current.shardsCounter.textContent || '0'
      const parsed = parseInt(text.replace(/[^\d]/g, ''), 10) // Remove any non-digit characters
      currentShards = !isNaN(parsed) && isFinite(parsed) ? parsed : 0
    }
    
    // Calculate the new value if not provided
    const calculatedNewValue = safeNewShardValue > 0 ? safeNewShardValue : (currentShards + safeAmount)
    
    // Note: Optimistic streak is now updated immediately by CompletionCelebration
    // when it shows with isFirstOfDay=true, so no need to update here
    
    // Floating wallet disabled - just show gem particles animation
    
    // Trigger gem particles (they'll fly to TopBar or fallback position)
    setGemAnimation({
      active: true,
      amount: safeAmount,
      targetPosition,
      newValue: calculatedNewValue,
    })
    
    // After gems arrive, trigger counter animation
    setTimeout(() => {
      setShardCounterUpdate({
        active: true,
        newValue: calculatedNewValue,
      })
      
      // Reset shard counter update after animation
      setTimeout(() => {
        setShardCounterUpdate({ active: false, newValue: calculatedNewValue })
      }, 800)
    }, 1200) // Gems take ~1200ms to reach target
    
    // Trigger streak explosion if first of day (visual explosion animation)
    if (isFirstOfDay) {
      setTimeout(() => {
        triggerStreakExplosion()
      }, 1400) // Slight delay after gems arrive for the visual explosion
    }
    
    // Reset gem animation
    setTimeout(() => {
      setGemAnimation({
        active: false,
        amount: 0,
        targetPosition: null,
        newValue: calculatedNewValue,
      })
    }, 2000)
  }, [])
  
  const triggerStreakExplosion = useCallback(() => {
    const targetPosition = getElementPosition(targetsRef.current.streakCounter)
    
    // Note: Optimistic streak delta is handled in triggerGemAnimation for immediate feedback
    // This function only handles the visual explosion animation
    
    setStreakExplosion({
      active: true,
      targetPosition,
    })
    
    // Reset after animation
    setTimeout(() => {
      setStreakExplosion({
        active: false,
        targetPosition: null,
      })
    }, 2000)
  }, [])
  
  const setOptimisticStreakIncrement = useCallback(() => {
    const today = getTodayDateString()
    
    // Only increment if we haven't already done an optimistic increment today
    if (streakIncrementDate === today) {
      // Already incremented today, skip
      return
    }
    
    // Mark that we've done an optimistic increment today
    setStreakIncrementDate(today)
    setOptimisticStreakDelta(prev => prev + 1)
    
    // Clear optimistic delta after data should have refreshed
    setTimeout(() => {
      setOptimisticStreakDelta(0)
    }, 5000)
  }, [streakIncrementDate])
  
  const clearOptimisticStreak = useCallback(() => {
    setOptimisticStreakDelta(0)
  }, [])
  
  // Mark that streak was already incremented today (called when we know server already counted it)
  const markStreakIncrementedToday = useCallback(() => {
    setStreakIncrementDate(getTodayDateString())
  }, [])
  
  // Called when floating wallet animation completes - dismiss it if still scrolled
  const onFloatingWalletComplete = useCallback(() => {
    // Check current scroll position - if user scrolled to top, let it stay
    // otherwise, dismiss it
    if (!isTopBarVisibleRef.current) {
      // Still scrolled down, dismiss the floating wallet
      setFloatingWallet(prev => ({ ...prev, active: false }))
    } else {
      // User scrolled to top, dismiss immediately (TopBar is visible now)
      setFloatingWallet(prev => ({ ...prev, active: false }))
    }
  }, [])
  
  // Split values into actions (stable) and state (changes frequently)
  const actionsValue: CelebrationAnimationActionsValue = {
    registerShardsCounter,
    registerStreakCounter,
    triggerGemAnimation,
    triggerStreakExplosion,
    setOptimisticStreakIncrement,
    clearOptimisticStreak,
    markStreakIncrementedToday,
    setTopBarVisible,
    onFloatingWalletComplete,
  }
  
  const stateValue: CelebrationAnimationStateValue = {
    optimisticStreakDelta,
    hasIncrementedStreakToday,
    gemAnimation,
    streakExplosion,
    shardCounterUpdate,
    floatingWallet,
    isTopBarVisible,
  }
  
  return (
    <CelebrationAnimationActionsContext.Provider value={actionsValue}>
      <CelebrationAnimationStateContext.Provider value={stateValue}>
        {children}
      </CelebrationAnimationStateContext.Provider>
    </CelebrationAnimationActionsContext.Provider>
  )
}

// Hook to access only actions (won't cause re-renders when animation state changes)
// Use this in components that only trigger animations
export function useCelebrationAnimationActions() {
  const context = useContext(CelebrationAnimationActionsContext)
  if (context === undefined) {
    throw new Error('useCelebrationAnimationActions must be used within a CelebrationAnimationProvider')
  }
  return context
}

// Hook to access only state (for components that render animations)
export function useCelebrationAnimationState() {
  const context = useContext(CelebrationAnimationStateContext)
  if (context === undefined) {
    throw new Error('useCelebrationAnimationState must be used within a CelebrationAnimationProvider')
  }
  return context
}

// Legacy hook that returns both (for backward compatibility)
// Prefer using the split hooks above for better performance
export function useCelebrationAnimation() {
  const actions = useCelebrationAnimationActions()
  const state = useCelebrationAnimationState()
  return { ...actions, ...state }
}
